<script setup>
// Draws the kit's FoldingGroup shape (components-next/polaris/cards/FoldingGroup.vue) around the
// woot's own accordion API. Kept as a separate component (not the kit one) because the header here
// doubles as the vuedraggable drag handle (`.drag-handle`) and click target — folding a section must
// not fight dragging it, so both live on the same element, as they did before this port.
import { useId } from 'vue';
import { useI18n } from 'vue-i18n';
import { ChevronDown16 } from '@carbon/icons-vue';
import { Badge } from 'next/polaris/ui/badge';
import EmojiOrIcon from 'shared/components/EmojiOrIcon.vue';

defineProps({
  title: {
    type: String,
    required: true,
  },
  compact: {
    type: Boolean,
    default: false,
  },
  icon: {
    type: String,
    default: '',
  },
  emoji: {
    type: String,
    default: '',
  },
  isOpen: {
    type: Boolean,
    default: true,
  },
  count: {
    type: Number,
    default: null,
  },
});

const emit = defineEmits(['toggle']);
const { t } = useI18n();
const bodyId = useId();

const onToggle = () => {
  emit('toggle');
};
</script>

<template>
  <div class="grid grid-cols-1 gap-3" data-accordion-item>
    <button
      type="button"
      class="drag-handle flex w-full cursor-grab select-none items-start gap-3 rounded-polaris-md text-start outline-none focus-visible:ring-[3px] focus-visible:ring-polaris-ring/50"
      :aria-label="
        t(
          isOpen
            ? 'POLARIS.FOLDING_GROUP.FOLD'
            : 'POLARIS.FOLDING_GROUP.UNFOLD',
          { title }
        )
      "
      :aria-expanded="isOpen ? 'true' : 'false'"
      :aria-controls="bodyId"
      @click.stop="onToggle"
    >
      <span
        v-if="icon || emoji"
        class="flex size-8 shrink-0 items-center justify-center rounded-full bg-polaris-muted text-polaris-foreground"
        aria-hidden="true"
      >
        <EmojiOrIcon class="inline-block w-5" :icon="icon" :emoji="emoji" />
      </span>
      <h5
        class="m-0 flex min-w-0 flex-1 items-center gap-2 type-polaris-card-title text-n-slate-12"
      >
        {{ title }}
        <Badge v-if="count !== null" variant="secondary">{{ count }}</Badge>
      </h5>
      <slot name="button" />
      <span
        class="flex size-8 shrink-0 items-center justify-center text-n-slate-11"
        aria-hidden="true"
      >
        <ChevronDown16
          class="transition-transform duration-150"
          :class="isOpen ? 'rotate-180' : ''"
        />
      </span>
    </button>
    <div
      v-if="isOpen"
      :id="bodyId"
      class="grid grid-cols-1 gap-3"
      :class="compact ? 'p-0' : 'px-2 py-4'"
    >
      <slot />
    </div>
  </div>
</template>
